import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Stack,
  Link,
  Tooltip
} from '@mui/material';
import { PiCheckCircle, PiXCircle, PiWarningCircle, PiArrowSquareOut } from 'react-icons/pi';

interface WorkflowResultDataProps {
  // map of stepName -> arbitrary step output object (e.g. { count, matches, summary })
  stepData: Record<string, any>;
}

// a single match row as returned by the cortese search
// (mirrors cortese_downloader.results_to_records)
interface CorteseMatch {
  source?: string;
  query_address?: string;
  site_name?: string;
  // the RECORD's own street address (not the queried one). "" when none is on file — about
  // 5.7% of Cortese records have no street number (all 25 solid-waste facilities included).
  address?: string;
  status?: string;
  city?: string;
  county?: string;
  latitude?: string;
  longitude?: string;
  // source-native record id (GeoTracker GLOBAL_ID, EnviroStor id, etc). when present we deep-link
  // straight to the record's profile page instead of a name/address search.
  global_id?: string;
  site_id?: string;
  record_id?: string;
  // the Cortese category that qualifies this site (e.g. "LUST Cleanup Site"). shown next to the
  // status so a reviewer can see WHY the site is on the list rather than trusting the tool.
  case_type?: string;
  [k: string]: any;
}

// the source-native record id for a match, under whatever key the handler used
function recordIdOf(m: CorteseMatch): string {
  const id = m.global_id || m.site_id || m.record_id || '';
  return String(id).trim();
}

// public search/listing pages for each Cortese source database
// (from westcoast-land-use/urls.md). the results data has no per-site ID, so instead of a
// generic listing we build an ADDRESS-SPECIFIC search on each source's public Cortese search.
// GeoTracker's search accepts main_street_name/city/zip/county; EnviroStor's CORTESE report
// accepts city/zip (ignored harmlessly if unsupported). CalEPA sources are static PDFs.

interface AddressParts {
  streetName: string;
  city: string;
  zip: string;
  county: string;
}

// pull street name (minus house number), city, zip, county from a match. city/county come
// straight off the match when present; street + zip are parsed from the queried address.
function addressPartsForMatch(m: CorteseMatch): AddressParts {
  const qa = (m.query_address || '').trim();
  const segments = qa.split(',').map((s) => s.trim());
  const firstSeg = segments[0] || '';
  const streetName = firstSeg.replace(/^\d+\s*/, '').trim(); // drop leading house number
  const zip = (qa.match(/\b\d{5}\b/) || [''])[0];
  // city: prefer the match's city field, else the 2nd address segment (before "ST 99999")
  const segCity = (segments[1] || '').replace(/\s+[A-Za-z]{2}\s*\d{5}.*$/, '').trim();
  const city = (m.city || segCity || '').trim();
  const county = (m.county || '').trim();
  return { streetName, city, zip, county };
}

const SOURCE_META: Record<
  string,
  {
    label: string;
    isSearch: boolean;
    // direct link to the record's profile page given its native id (undefined = no profile page)
    profileUrl?: (id: string) => string;
    buildUrl: (p: AddressParts, m: CorteseMatch) => string;
  }
> = {
  geotracker: {
    label: 'GeoTracker (Water Boards)',
    isSearch: true,
    profileUrl: (id) =>
      `https://geotracker.waterboards.ca.gov/profile_report?global_id=${encodeURIComponent(id)}`,
    // pinpoint the matched record by its facility name (GeoTracker's business_name field maps to
    // the "SITE / FACILITY NAME" column) so the search confirms the exact match, not just the
    // street. falls back to a street-name search when the match has no site name.
    buildUrl: (p, m) => {
      const base = 'https://geotracker.waterboards.ca.gov/search?CMD=search&case_number=';
      const tail = '&SITE_TYPE=&oilfield=&STATUS=&BRANCH=&MASTER_BASE=&Search=Search';
      if (m.site_name) {
        return (
          base +
          `&business_name=${encodeURIComponent(m.site_name)}` +
          '&main_street_name=' +
          `&city=${encodeURIComponent(p.city)}` +
          '&zip=' +
          `&county=${encodeURIComponent(p.county)}` +
          tail
        );
      }
      return (
        base +
        '&business_name=' +
        `&main_street_name=${encodeURIComponent(p.streetName)}` +
        `&city=${encodeURIComponent(p.city)}` +
        `&zip=${encodeURIComponent(p.zip)}` +
        `&county=${encodeURIComponent(p.county)}` +
        tail
      );
    }
  },
  envirostor: {
    label: 'EnviroStor (DTSC)',
    isSearch: true,
    profileUrl: (id) =>
      `https://www.envirostor.dtsc.ca.gov/public/profile_report?global_id=${encodeURIComponent(id)}`,
    // EnviroStor's advanced search matches a facility/site name; use the matched site name to
    // pinpoint the record, scoped to the city. falls back to a city/zip filter on the CORTESE report.
    buildUrl: (p, m) =>
      m.site_name
        ? 'https://www.envirostor.dtsc.ca.gov/public/search?cmd=search' +
          `&business_name=${encodeURIComponent(m.site_name)}` +
          `&city=${encodeURIComponent(p.city)}`
        : 'https://www.envirostor.dtsc.ca.gov/public/search?cmd=search&reporttype=CORTESE' +
          '&site_type=CSITES,FUDS&status=ACT,BKLG,COM' +
          `&city=${encodeURIComponent(p.city)}` +
          `&zip=${encodeURIComponent(p.zip)}`
  },
  solid_waste: {
    label: 'CalEPA Solid Waste (PDF)',
    isSearch: false,
    buildUrl: () =>
      'https://calepa.ca.gov/wp-content/uploads/2026/02/CorteseList-Solid-Waste-Facilities_2026.pdf'
  },
  scp_cortese: {
    label: 'CalEPA Site Cleanup Program (PDF)',
    isSearch: false,
    // scp_cortese rows generally carry a GeoTracker-style ID, so when one is present we can
    // deep-link to the GeoTracker record instead of the (large) source PDF.
    profileUrl: (id) =>
      `https://geotracker.waterboards.ca.gov/profile_report?global_id=${encodeURIComponent(id)}`,
    buildUrl: () =>
      'https://calepa.ca.gov/wp-content/uploads/2026/03/2026_scp_cortese.list_lat.long_.pdf'
  }
};

// build a Google Maps link for a match using the human-readable ADDRESS the user searched
// (the input address), not raw coordinates. falls back to the matched site name + city if the
// queried address isn't available.
function mapUrlForMatch(m: CorteseMatch): string | null {
  const address =
    (m.query_address && m.query_address.trim()) ||
    [m.site_name, m.city, m.county].filter(Boolean).join(', ');
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

// renders inline data returned by workflow steps that produce no output files
// (e.g. the cortese_list step returns { count, matches[], summary }). shows a summary
// line, a count chip, and any array-of-objects as a table.
export default function WorkflowResultData({ stepData }: WorkflowResultDataProps) {
  if (!stepData || Object.keys(stepData).length === 0) {
    return null;
  }

  const formatStepName = (name: string) =>
    name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // detects the cortese-style result shape: a matches[] array whose rows carry
  // query_address / source (produced by cortese_downloader.results_to_records)
  const isCorteseResult = (data: any): boolean =>
    Array.isArray(data?.matches) &&
    (data.matches.length === 0 ||
      (typeof data.matches[0] === 'object' &&
        ('query_address' in data.matches[0] || 'source' in data.matches[0])));

  // renders results in the spirit of the original CLI: a headline (how many matches),
  // a per-address breakdown (where each address matched), then a detail table.
  const renderCorteseResult = (data: any) => {
    const matches: CorteseMatch[] = data.matches || [];
    const count = typeof data.count === 'number' ? data.count : matches.length;

    // the addresses that were searched. the handler now returns a `searched[]` array of
    // { address, variations[] } (search transparency, so a "Clear" result is auditable).
    // fall back to the older searched_addresses / addresses string arrays, then to the
    // distinct query_address values on the matches.
    const searchedEntries: { address: string; variations: string[] }[] = Array.isArray(data.searched)
      ? data.searched
          .map((s: any) =>
            typeof s === 'string'
              ? { address: s, variations: [] }
              : { address: String(s?.address || ''), variations: Array.isArray(s?.variations) ? s.variations : [] }
          )
          .filter((s: { address: string }) => !!s.address)
      : [];

    // address -> the variations actually tried, for the no-hit audit trail
    const variationsByAddress = new Map<string, string[]>();
    for (const s of searchedEntries) variationsByAddress.set(s.address, s.variations);

    const searchedRaw: string[] =
      (searchedEntries.length > 0 && searchedEntries.map((s) => s.address)) ||
      (Array.isArray(data.searched_addresses) && data.searched_addresses) ||
      (Array.isArray(data.addresses) && data.addresses) ||
      [];
    const fromMatches = Array.from(
      new Set(matches.map((m) => m.query_address).filter(Boolean) as string[])
    );
    const searched = searchedRaw.length > 0 ? searchedRaw : fromMatches;

    // group matches by the address they were found for
    const byAddress = new Map<string, CorteseMatch[]>();
    for (const m of matches) {
      const key = m.query_address || '(unknown address)';
      if (!byAddress.has(key)) byAddress.set(key, []);
      byAddress.get(key)!.push(m);
    }

    // sort matched addresses to the top so the flagged ones lead (serial-position effect:
    // users scan the first items first, and matches are what matter for a compliance check).
    const orderedAddresses = [...searched].sort((a, b) => {
      const am = (byAddress.get(a) || []).length > 0 ? 0 : 1;
      const bm = (byAddress.get(b) || []).length > 0 ? 0 : 1;
      return am - bm;
    });

    // how many of the searched addresses were flagged (the key compliance number)
    const flaggedCount = searched.filter((a) => (byAddress.get(a) || []).length > 0).length;
    const totalAddresses = searched.length || byAddress.size;
    const sourceCount = new Set(matches.map((m) => m.source).filter(Boolean)).size;

    // area search results (present only when a zip/city/county was requested)
    const areaRecords: CorteseMatch[] = Array.isArray(data.area?.records) ? data.area.records : [];
    const areaQuery = (data.area?.query || {}) as Record<string, any>;
    const areaLabel = ['zip', 'city', 'county']
      .filter((k) => areaQuery[k])
      .map((k) => `${k}: ${areaQuery[k]}`)
      .join(', ');

    // renders the detail for a single match: site name -> map link, source -> DB link, status.
    // pass hideSiteName when the caller already shows the name in its own column (area table).
    const renderMatchDetail = (m: CorteseMatch, key: React.Key, hideSiteName = false) => {
      const meta = m.source ? SOURCE_META[m.source] : undefined;
      // when the match carries a source-native record id, deep-link straight to the exact record
      // (definitive confirmation, works for closed cases too); otherwise fall back to a search.
      const recordId = recordIdOf(m);
      const isDirect = !!(meta && meta.profileUrl && recordId);
      const srcUrl = meta
        ? isDirect
          ? meta.profileUrl!(recordId)
          : meta.buildUrl(addressPartsForMatch(m), m)
        : undefined;
      const srcTooltip = meta
        ? isDirect
          ? `Open the exact ${meta.label} record (ID ${recordId})`
          : meta.isSearch
          ? m.site_name
            ? `Confirm this match on ${meta.label} — searches for "${m.site_name}"`
            : `Confirm this match on ${meta.label} — searches for the input address`
          : `Open ${meta.label}`
        : undefined;
      const mapUrl = mapUrlForMatch(m);
      const place = [m.city, m.county].filter(Boolean).join(', ');
      return (
        <Box key={key} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, py: 0.25 }}>
          {!hideSiteName && m.site_name ? (
            mapUrl ? (
              <Tooltip title="View the input address on Google Maps">
                <Link
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {m.site_name}
                  <PiArrowSquareOut size={13} style={{ opacity: 0.7 }} />
                </Link>
              </Tooltip>
            ) : (
              <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                {m.site_name}
              </Typography>
            )
          ) : null}
          {m.case_type && (
            <Tooltip title="The Cortese List category this site qualifies under">
              <Chip
                label={m.case_type}
                size="small"
                variant="outlined"
                color="default"
                sx={{ height: 20 }}
              />
            </Tooltip>
          )}
          {m.address && (
            <Typography variant="caption" color="text.secondary">
              {m.address}
            </Typography>
          )}
          {m.status && (
            <Typography variant="caption" color="text.secondary">
              {m.status}
            </Typography>
          )}
          {place && (
            <Typography variant="caption" color="text.secondary">
              · {place}
            </Typography>
          )}
          {meta && srcUrl ? (
            <Tooltip title={srcTooltip}>
              <Chip
                label={isDirect ? `${m.source} \u2713` : m.source}
                size="small"
                color="primary"
                variant={isDirect ? 'filled' : 'outlined'}
                clickable
                icon={<PiArrowSquareOut size={12} />}
                component="a"
                href={srcUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{ height: 20 }}
              />
            </Tooltip>
          ) : m.source ? (
            <Chip label={m.source} size="small" variant="outlined" sx={{ height: 20 }} />
          ) : null}
        </Box>
      );
    };

    return (
      <Box>
        {/* headline: lead with the key numbers (chunking / Miller's law) */}
        <Alert
          severity={count > 0 ? 'warning' : 'success'}
          icon={count > 0 ? <PiXCircle /> : <PiCheckCircle />}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {count > 0
              ? `${flaggedCount} of ${totalAddresses} address${totalAddresses === 1 ? '' : 'es'} flagged on the Cortese List`
              : 'No matches — none of the addresses appear on the Cortese List'}
          </Typography>
          {count > 0 && (
            <Typography variant="body2">
              {count} total match{count === 1 ? '' : 'es'} across {sourceCount} source{sourceCount === 1 ? '' : 's'}
            </Typography>
          )}
        </Alert>

        {/* one unified table: a row per checked address (flagged rows sorted to the top).
            deliberately NOT height-capped — a hidden inner scroll area cut off the last
            address mid-row with no visual cue. the job card itself scrolls. */}
        {totalAddresses > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 110 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Matches</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderedAddresses.map((addr, i) => {
                  const addrMatches = byAddress.get(addr) || [];
                  const flagged = addrMatches.length > 0;
                  return (
                    <TableRow
                      key={i}
                      hover
                      sx={flagged ? { backgroundColor: 'rgba(237,108,2,0.06)' } : undefined}
                    >
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Chip
                          size="small"
                          icon={flagged ? <PiWarningCircle /> : <PiCheckCircle />}
                          label={flagged ? `Flagged` : 'Clear'}
                          color={flagged ? 'warning' : 'success'}
                          variant={flagged ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>{addr}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {flagged ? (
                          <Stack spacing={0.5}>
                            {addrMatches.map((m, idx) => renderMatchDetail(m, idx))}
                          </Stack>
                        ) : (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              No records found
                            </Typography>
                            {/* search transparency: show the variations actually tried so a
                                "Clear" result is auditable instead of a black box */}
                            {(variationsByAddress.get(addr) || []).length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                searched: {(variationsByAddress.get(addr) || []).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* area search: every Cortese record in a requested zip/city/county. covers the ~5.7%
            of records with no street number (incl. all 25 solid-waste facilities), which
            per-address matching can never reach. */}
        {areaRecords.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              All Cortese records in {areaLabel || 'the requested area'}
              {typeof data.area?.count === 'number' ? ` (${data.area.count})` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Includes sites with no street address, which a per-address search cannot match.
              {areaRecords.length > 8 ? ' Scroll the list to see all records.' : ''}
            </Typography>
            {/* capped because an area query can return hundreds of records. the inset shadow
                at the bottom edge signals there is more to scroll (otherwise the last row
                looks like the end of the list). */}
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: 420,
                backgroundImage:
                  'linear-gradient(to top, rgba(0,0,0,0.08), rgba(0,0,0,0) 24px)',
                backgroundAttachment: 'local, scroll'
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {areaRecords.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ verticalAlign: 'top', fontWeight: 500 }}>
                        {r.site_name || '\u2014'}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {renderMatchDetail(r, i, true)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* data vintage — how current the source data was when the check ran */}
        {typeof data.source_downloaded === 'string' && data.source_downloaded.trim() && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Cortese List source data as of {data.source_downloaded}
          </Typography>
        )}
      </Box>
    );
  };


  const renderTable = (rows: any[]) => {
    // collect the union of keys across rows for column headers, preserving first-seen order
    const columns: string[] = [];
    for (const row of rows) {
      if (row && typeof row === 'object') {
        for (const key of Object.keys(row)) {
          if (!columns.includes(key)) columns.push(key);
        }
      }
    }

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          mt: 1,
          maxHeight: 480,
          // bottom-edge shadow signals more rows below when the list is scrollable
          backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.08), rgba(0,0,0,0) 24px)',
          backgroundAttachment: 'local, scroll'
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {formatStepName(col)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx} hover>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ fontSize: '0.8rem' }}>
                    {typeof row?.[col] === 'object'
                      ? JSON.stringify(row[col])
                      : String(row?.[col] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      {Object.entries(stepData).map(([stepName, data]) => {
        if (!data || typeof data !== 'object') return null;

        // cortese-style results get a purpose-built view (headline + per-address + table)
        if (isCorteseResult(data)) {
          return (
            <Box key={stepName} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {formatStepName(stepName)}
              </Typography>
              {renderCorteseResult(data)}
            </Box>
          );
        }

        // find the first array-of-objects field to render as a table (e.g. matches)
        const arrayField = Object.entries(data).find(
          ([, v]) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object'
        );

        return (
          <Box key={stepName} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formatStepName(stepName)}
              </Typography>
              {typeof data.count === 'number' && (
                <Chip
                  label={`${data.count} ${data.count === 1 ? 'match' : 'matches'}`}
                  size="small"
                  color={data.count > 0 ? 'primary' : 'default'}
                />
              )}
            </Box>

            {typeof data.summary === 'string' && data.summary.trim() && (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', mb: arrayField ? 1 : 0 }}
              >
                {data.summary}
              </Typography>
            )}

            {arrayField && renderTable(arrayField[1] as any[])}

            {/* if there's no summary and no table, fall back to raw JSON */}
            {!data.summary && !arrayField && (
              <Box
                component="pre"
                sx={{
                  fontSize: '0.8rem',
                  bgcolor: 'action.hover',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  m: 0
                }}
              >
                {JSON.stringify(data, null, 2)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
